/**
 * useStreamingTranscription Hook
 * Orchestrates AWS Transcribe Streaming (browser-direct) + WebSocket session management.
 *
 * Manages the full lifecycle:
 * 1. getUserMedia → AudioWorklet → PCM chunks
 * 2. PCM chunks → TranscribeStreamingSession → transcript events
 * 3. Transcript events → WebSocket session persistence
 * 4. Session lifecycle (start, pause, resume, stop)
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { useCapabilities } from '@/contexts/CapabilitiesContext';
import {
  TranscribeStreamingSession,
  type TranscriptFragment,
} from '@/services/transcribeStreamingService';
import {
  StreamingSessionManager,
  type WebSocketMessage,
  type StreamingSession,
} from '@/services/streamingSessionService';
import { addApi } from '@/services/addApi';
import { createLogger } from '@/lib/logger';

const log = createLogger('useStreamingTranscription');

// Upload accumulated audio chunks to S3 every 30 seconds during streaming
const CHUNK_UPLOAD_INTERVAL_MS = 30_000;

export type SessionStatus = 'idle' | 'connecting' | 'active' | 'paused' | 'stopped' | 'error';

export interface UseStreamingTranscriptionReturn {
  sessionId: string | null;
  sessionStatus: SessionStatus;
  liveTranscript: string;
  finalizedTranscript: string;
  transcriptFragments: TranscriptFragment[];
  startStreaming: (languageCode: string) => Promise<void>;
  pauseStreaming: () => void;
  resumeStreaming: () => Promise<void>;
  stopStreaming: (params: {
    projectId: string;
    projectName: string;
    templateId: string;
    templateName: string;
    outputFormat: string;
    identityId: string;
  }) => Promise<string | null>;
  discardStreaming: () => void;
  isTranscribeConnected: boolean;
  isWebSocketConnected: boolean;
  interruptedSessions: StreamingSession[];
  loadInterruptedSessions: () => Promise<void>;
  resumeInterruptedSession: (sessionId: string, languageCode: string) => Promise<void>;
  duration: number;
  getAnalyserNode: () => AnalyserNode | null;
  audioChunkKeys: string[];
}

export function useStreamingTranscription(): UseStreamingTranscriptionReturn {
  const { getAccessToken } = useAuthContext();
  const { hasCapability } = useCapabilities();
  const canPersistAudio = hasCapability('sync_to_cloud');

  // Session state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>('idle');
  const [liveTranscript, setLiveTranscript] = useState('');
  const [finalizedTranscript, setFinalizedTranscript] = useState('');
  const [transcriptFragments, setTranscriptFragments] = useState<TranscriptFragment[]>([]);
  const [isTranscribeConnected, setIsTranscribeConnected] = useState(false);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  const [interruptedSessions, setInterruptedSessions] = useState<StreamingSession[]>([]);
  const [duration, setDuration] = useState(0);

  // Refs for audio pipeline
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const transcribeSessionRef = useRef<TranscribeStreamingSession | null>(null);
  const wsManagerRef = useRef<StreamingSessionManager | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedDurationRef = useRef<number>(0);
  const durationRef = useRef<number>(0);
  const jobIdResolveRef = useRef<((jobId: string | null) => void) | null>(null);

  // Pro plan: parallel MediaRecorder for audio persistence
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioChunkKeysRef = useRef<string[]>([]);
  const chunkUploadIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const chunkSequenceRef = useRef<number>(0);

  // Cleanup timer
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Start duration timer
  const startTimer = useCallback(() => {
    clearTimer();
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000) + pausedDurationRef.current;
      durationRef.current = elapsed;
      setDuration(elapsed);
    }, 1000);
  }, [clearTimer]);

  // Initialize WebSocket manager
  const getWsManager = useCallback((): StreamingSessionManager => {
    if (!wsManagerRef.current) {
      wsManagerRef.current = new StreamingSessionManager(getAccessToken, {
        onMessage: (message: WebSocketMessage) => {
          switch (message.type) {
            case 'session.started':
              setSessionId(message.sessionId);
              setSessionStatus('active');
              log.info('Session started', { sessionId: message.sessionId });
              break;

            case 'session.resumed':
              setSessionId(message.sessionId);
              setSessionStatus('active');
              setFinalizedTranscript(message.existingTranscript || '');
              pausedDurationRef.current = message.durationSeconds || 0;
              log.info('Session resumed', { sessionId: message.sessionId, fragmentCount: message.fragmentCount });
              break;

            case 'session.stopped':
              setSessionStatus('stopped');
              if (jobIdResolveRef.current) {
                jobIdResolveRef.current(message.jobId || null);
                jobIdResolveRef.current = null;
              }
              log.info('Session stopped', { sessionId: message.sessionId, jobId: message.jobId });
              break;

            case 'session.timeout':
              log.warn('Session timeout — max duration reached', {
                sessionId: message.sessionId,
                maxDurationSeconds: message.maxDurationSeconds,
              });
              // Stop Transcribe immediately to stop AWS billing
              transcribeSessionRef.current?.stop();
              transcribeSessionRef.current = null;
              setIsTranscribeConnected(false);
              // Stop microphone
              streamRef.current?.getTracks().forEach(track => track.stop());
              // Stop MediaRecorder
              if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
              }
              if (chunkUploadIntervalRef.current) {
                clearInterval(chunkUploadIntervalRef.current);
                chunkUploadIntervalRef.current = null;
              }
              clearTimer();
              setSessionStatus('stopped');
              break;

            case 'error':
              log.error('WebSocket error message', new Error(message.message));
              break;
          }
        },
        onConnected: () => {
          setIsWebSocketConnected(true);
        },
        onDisconnected: () => {
          setIsWebSocketConnected(false);
        },
        onReconnecting: (attempt) => {
          log.info(`WebSocket reconnecting (attempt ${attempt})`);
        },
        onReconnectFailed: () => {
          log.warn('WebSocket reconnection failed');
        },
      });
    }
    return wsManagerRef.current;
  }, [getAccessToken]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupAudioPipeline();
      wsManagerRef.current?.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanupAudioPipeline = useCallback(() => {
    clearTimer();
    transcribeSessionRef.current?.stop();
    transcribeSessionRef.current = null;
    setIsTranscribeConnected(false);

    // Clear periodic chunk upload interval
    if (chunkUploadIntervalRef.current) {
      clearInterval(chunkUploadIntervalRef.current);
      chunkUploadIntervalRef.current = null;
    }

    // Stop parallel MediaRecorder (Pro plan)
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;

    if (workletNodeRef.current) {
      workletNodeRef.current.port.postMessage('stop');
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    analyserRef.current = null;
  }, [clearTimer]);

  // Start streaming
  const startStreaming = useCallback(async (languageCode: string) => {
    setSessionStatus('connecting');
    setLiveTranscript('');
    setFinalizedTranscript('');
    setTranscriptFragments([]);
    setDuration(0);
    durationRef.current = 0;
    pausedDurationRef.current = 0;

    try {
      // 1. Get microphone
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: { ideal: 48000 } },
      });
      streamRef.current = stream;

      // 2. Set up AudioContext + WorkletNode
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);

      // Set up analyser for level meter
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      // Load AudioWorklet
      await audioContext.audioWorklet.addModule('/audio-resampler-worklet.js');
      const workletNode = new AudioWorkletNode(audioContext, 'audio-resampler-processor');
      workletNodeRef.current = workletNode;
      source.connect(workletNode);

      // 3. Pro plan: start parallel MediaRecorder for audio persistence with periodic chunk uploads
      if (canPersistAudio && stream) {
        audioChunksRef.current = [];
        audioChunkKeysRef.current = [];
        chunkSequenceRef.current = 0;
        const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
        const recorder = new MediaRecorder(stream, { mimeType });
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) audioChunksRef.current.push(e.data);
        };
        recorder.start(1000);
        mediaRecorderRef.current = recorder;

        // Periodic chunk upload: every 30s, assemble accumulated chunks into a blob and upload to S3
        chunkUploadIntervalRef.current = setInterval(async () => {
          const chunks = audioChunksRef.current;
          if (chunks.length === 0) return;

          // Take current chunks and reset accumulator
          const chunksToUpload = [...chunks];
          audioChunksRef.current = [];

          const seq = chunkSequenceRef.current++;
          const contentType = mimeType;
          const extension = contentType.includes('webm') ? 'webm' : 'mp4';
          const filename = `streaming-chunk-${String(seq).padStart(3, '0')}.${extension}`;

          try {
            const blob = new Blob(chunksToUpload, { type: contentType });
            const uploadResponse = await addApi.getUploadUrl(filename, contentType);
            await addApi.uploadMedia(uploadResponse.presignedUrl, blob, contentType);
            audioChunkKeysRef.current.push(uploadResponse.s3Key);
            log.info('Uploaded audio chunk to S3', { seq, s3Key: uploadResponse.s3Key, size: blob.size });
          } catch (err) {
            // Put chunks back so they'll be included in the next upload or final upload
            audioChunksRef.current = [...chunksToUpload, ...audioChunksRef.current];
            log.warn('Failed to upload audio chunk (will retry)', { seq, error: String(err) });
          }
        }, CHUNK_UPLOAD_INTERVAL_MS);
      }

      // 4. Connect to WebSocket
      const wsManager = getWsManager();
      await wsManager.connect();

      // 5. Start Transcribe Streaming session
      const transcribeSession = new TranscribeStreamingSession(languageCode, {
        onTranscript: (fragment: TranscriptFragment) => {
          if (fragment.isFinal) {
            setFinalizedTranscript(prev => {
              const separator = prev ? ' ' : '';
              return prev + separator + fragment.text;
            });
            setLiveTranscript('');
            setTranscriptFragments(prev => [...prev, fragment]);

            // Persist final fragment to backend
            wsManager.sendTranscriptFragment(fragment, durationRef.current);
          } else {
            setLiveTranscript(fragment.text);
          }
        },
        onError: (error: Error) => {
          log.error('Transcribe Streaming error', error);
          setIsTranscribeConnected(false);
        },
        onClose: () => {
          setIsTranscribeConnected(false);
        },
      });
      transcribeSessionRef.current = transcribeSession;

      // Route PCM chunks from worklet to Transcribe
      workletNode.port.onmessage = (event: MessageEvent<Uint8Array>) => {
        transcribeSession.pushAudio(event.data);
      };

      // Start Transcribe (async, runs in background)
      transcribeSession.start().catch((err) => {
        log.error('Transcribe session failed', err instanceof Error ? err : new Error(String(err)));
      });
      setIsTranscribeConnected(true);

      // 6. Start WebSocket session
      wsManager.startSession(languageCode);

      // 7. Start duration timer
      startTimer();

    } catch (error) {
      log.error('Failed to start streaming', error instanceof Error ? error : new Error(String(error)));
      cleanupAudioPipeline();
      setSessionStatus('error');
      throw error;
    }
  }, [getWsManager, startTimer, cleanupAudioPipeline, canPersistAudio]);

  // Pause streaming
  const pauseStreaming = useCallback(() => {
    clearTimer();
    pausedDurationRef.current = durationRef.current;

    // Pause the audio processing (mute the worklet input)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => { track.enabled = false; });
    }

    // Pause parallel MediaRecorder (Pro plan)
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
    }

    // Pause periodic chunk uploads
    if (chunkUploadIntervalRef.current) {
      clearInterval(chunkUploadIntervalRef.current);
      chunkUploadIntervalRef.current = null;
    }

    setSessionStatus('paused');
  }, [clearTimer]);

  // Resume streaming
  const resumeStreaming = useCallback(async () => {
    // Re-enable audio tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => { track.enabled = true; });
    }

    // Resume parallel MediaRecorder (Pro plan)
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
    }

    // Restart periodic chunk uploads
    if (canPersistAudio && mediaRecorderRef.current) {
      chunkUploadIntervalRef.current = setInterval(async () => {
        const chunks = audioChunksRef.current;
        if (chunks.length === 0) return;

        const chunksToUpload = [...chunks];
        audioChunksRef.current = [];

        const seq = chunkSequenceRef.current++;
        const contentType = mediaRecorderRef.current?.mimeType || 'audio/webm';
        const extension = contentType.includes('webm') ? 'webm' : 'mp4';
        const filename = `streaming-chunk-${String(seq).padStart(3, '0')}.${extension}`;

        try {
          const blob = new Blob(chunksToUpload, { type: contentType });
          const uploadResponse = await addApi.getUploadUrl(filename, contentType);
          await addApi.uploadMedia(uploadResponse.presignedUrl, blob, contentType);
          audioChunkKeysRef.current.push(uploadResponse.s3Key);
          log.info('Uploaded audio chunk to S3', { seq, s3Key: uploadResponse.s3Key });
        } catch (err) {
          audioChunksRef.current = [...chunksToUpload, ...audioChunksRef.current];
          log.warn('Failed to upload audio chunk (will retry)', { seq, error: String(err) });
        }
      }, CHUNK_UPLOAD_INTERVAL_MS);
    }

    startTimer();
    setSessionStatus('active');
  }, [startTimer, canPersistAudio]);

  // Stop streaming and submit to processing pipeline
  const stopStreaming = useCallback(async (params: {
    projectId: string;
    projectName: string;
    templateId: string;
    templateName: string;
    outputFormat: string;
    identityId: string;
  }): Promise<string | null> => {
    clearTimer();

    // Stop periodic chunk upload interval
    if (chunkUploadIntervalRef.current) {
      clearInterval(chunkUploadIntervalRef.current);
      chunkUploadIntervalRef.current = null;
    }

    // Pro plan: stop MediaRecorder and upload remaining audio chunks to S3
    let audioS3Key: string | undefined;
    const allChunkKeys = [...audioChunkKeysRef.current];

    if (canPersistAudio && mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        // Stop recorder and wait for final data
        const blobPromise = new Promise<Blob>((resolve) => {
          const recorder = mediaRecorderRef.current!;
          const mimeType = recorder.mimeType;
          recorder.onstop = () => {
            const blob = new Blob(audioChunksRef.current, { type: mimeType });
            resolve(blob);
          };
          recorder.stop();
        });

        const audioBlob = await blobPromise;
        if (audioBlob.size > 0) {
          const contentType = audioBlob.type || 'audio/webm';
          const extension = contentType.includes('webm') ? 'webm' : 'mp4';

          if (allChunkKeys.length > 0) {
            // We already uploaded periodic chunks — upload remaining as the final chunk
            const seq = chunkSequenceRef.current++;
            const filename = `streaming-chunk-${String(seq).padStart(3, '0')}.${extension}`;
            const uploadResponse = await addApi.getUploadUrl(filename, contentType);
            await addApi.uploadMedia(uploadResponse.presignedUrl, audioBlob, contentType);
            allChunkKeys.push(uploadResponse.s3Key);
            log.info('Uploaded final audio chunk to S3', { seq, s3Key: uploadResponse.s3Key });
          } else {
            // No periodic chunks were uploaded (recording < 30s) — upload as single file
            const filename = `streaming-recording.${extension}`;
            const uploadResponse = await addApi.getUploadUrl(filename, contentType);
            await addApi.uploadMedia(uploadResponse.presignedUrl, audioBlob, contentType);
            audioS3Key = uploadResponse.s3Key;
            log.info('Pro audio uploaded to S3 (single file)', { audioS3Key });
          }
        }
      } catch (err) {
        log.warn('Failed to upload Pro audio (non-blocking)', { error: String(err) });
      }
    }

    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    audioChunkKeysRef.current = [];
    chunkSequenceRef.current = 0;

    // Upload assembled transcript to S3
    let transcriptS3Key: string | undefined;
    try {
      const currentTranscript = finalizedTranscript;
      if (currentTranscript) {
        const transcriptBlob = new Blob([currentTranscript], { type: 'text/plain' });
        const uploadResponse = await addApi.getUploadUrl('streaming-transcript.txt', 'text/plain');
        await addApi.uploadMedia(uploadResponse.presignedUrl, transcriptBlob, 'text/plain');
        transcriptS3Key = uploadResponse.s3Key;
        log.info('Transcript uploaded to S3', { transcriptS3Key });
      }
    } catch (err) {
      log.warn('Failed to upload transcript to S3 (non-blocking)', { error: String(err) });
    }

    // Clean up Transcribe + audio pipeline (but not MediaRecorder, already handled above)
    transcribeSessionRef.current?.stop();
    transcribeSessionRef.current = null;
    setIsTranscribeConnected(false);

    if (workletNodeRef.current) {
      workletNodeRef.current.port.postMessage('stop');
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    analyserRef.current = null;

    // Wait for jobId from WebSocket response
    const jobIdPromise = new Promise<string | null>((resolve) => {
      jobIdResolveRef.current = resolve;
      // Timeout after 30 seconds
      setTimeout(() => {
        if (jobIdResolveRef.current === resolve) {
          jobIdResolveRef.current = null;
          resolve(null);
        }
      }, 30000);
    });

    const wsManager = getWsManager();
    wsManager.stopSession({
      ...params,
      ...(audioS3Key ? { audioS3Key } : {}),
      ...(allChunkKeys.length > 0 ? { audioChunkKeys: allChunkKeys } : {}),
      ...(transcriptS3Key ? { transcriptS3Key } : {}),
    });

    return jobIdPromise;
  }, [clearTimer, canPersistAudio, getWsManager, finalizedTranscript]);

  // Discard streaming without submitting
  const discardStreaming = useCallback(() => {
    cleanupAudioPipeline();
    audioChunksRef.current = [];
    audioChunkKeysRef.current = [];
    chunkSequenceRef.current = 0;
    wsManagerRef.current?.disconnect();
    wsManagerRef.current = null;

    setSessionId(null);
    setSessionStatus('idle');
    setLiveTranscript('');
    setFinalizedTranscript('');
    setTranscriptFragments([]);
    setDuration(0);
    durationRef.current = 0;
    pausedDurationRef.current = 0;
  }, [cleanupAudioPipeline]);

  // Load interrupted sessions
  const loadInterruptedSessions = useCallback(async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      const wsManager = getWsManager();
      const sessions = await wsManager.getInterruptedSessions(token);
      setInterruptedSessions(sessions);
    } catch (error) {
      log.warn('Failed to load interrupted sessions', { error: String(error) });
    }
  }, [getAccessToken, getWsManager]);

  // Resume an interrupted session
  const resumeInterruptedSession = useCallback(async (resumeSessionId: string, languageCode: string) => {
    setSessionStatus('connecting');
    setLiveTranscript('');
    setDuration(0);
    durationRef.current = 0;

    try {
      // 1. Get microphone
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: { ideal: 48000 } },
      });
      streamRef.current = stream;

      // 2. Set up AudioContext + WorkletNode
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      await audioContext.audioWorklet.addModule('/audio-resampler-worklet.js');
      const workletNode = new AudioWorkletNode(audioContext, 'audio-resampler-processor');
      workletNodeRef.current = workletNode;
      source.connect(workletNode);

      // 3. Connect WebSocket
      const wsManager = getWsManager();
      await wsManager.connect();

      // 4. Start Transcribe Streaming
      const transcribeSession = new TranscribeStreamingSession(languageCode, {
        onTranscript: (fragment: TranscriptFragment) => {
          if (fragment.isFinal) {
            setFinalizedTranscript(prev => {
              const separator = prev ? ' ' : '';
              return prev + separator + fragment.text;
            });
            setLiveTranscript('');
            setTranscriptFragments(prev => [...prev, fragment]);
            wsManager.sendTranscriptFragment(fragment, durationRef.current);
          } else {
            setLiveTranscript(fragment.text);
          }
        },
        onError: (error: Error) => {
          log.error('Transcribe error on resume', error);
          setIsTranscribeConnected(false);
        },
        onClose: () => setIsTranscribeConnected(false),
      });
      transcribeSessionRef.current = transcribeSession;

      workletNode.port.onmessage = (event: MessageEvent<Uint8Array>) => {
        transcribeSession.pushAudio(event.data);
      };

      transcribeSession.start().catch((err) => {
        log.error('Transcribe session failed on resume', err instanceof Error ? err : new Error(String(err)));
      });
      setIsTranscribeConnected(true);

      // 5. Resume the WebSocket session
      wsManager.startSession(languageCode, resumeSessionId);

      // 6. Start timer
      startTimer();

      // Remove from interrupted list
      setInterruptedSessions(prev => prev.filter(s => s.sessionId !== resumeSessionId));

    } catch (error) {
      log.error('Failed to resume session', error instanceof Error ? error : new Error(String(error)));
      cleanupAudioPipeline();
      setSessionStatus('error');
      throw error;
    }
  }, [getWsManager, startTimer, cleanupAudioPipeline]);

  const getAnalyserNode = useCallback(() => {
    return analyserRef.current;
  }, []);

  return {
    sessionId,
    sessionStatus,
    liveTranscript,
    finalizedTranscript,
    transcriptFragments,
    startStreaming,
    pauseStreaming,
    resumeStreaming,
    stopStreaming,
    discardStreaming,
    isTranscribeConnected,
    isWebSocketConnected,
    interruptedSessions,
    loadInterruptedSessions,
    resumeInterruptedSession,
    duration,
    getAnalyserNode,
    audioChunkKeys: audioChunkKeysRef.current,
  };
}
